/* @flow */

import * as React from 'react';
import classNames from 'classnames';
import { compose } from 'ramda';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import styles from './MainTopBar.style';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

// Hooks
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import useSelf from 'src/shared/domain/user/hook/useSelf';

// Utils
import messages from 'src/shared/app/nav/l10n/navigationL10n';
import { getNotificationCount } from 'src/shared/app/notification/selector/appNotificationSelectors';
import {
  newTopicRoute,
  peopleFeedRoute,
  preferencesRoute,
  profileRoute,
  ownTopicsRoute,
  availabilityRoute,
  profileEditRoute,
} from 'src/shared/app/base/route/setting/routeSettings';

import MainNav from 'src/shared/app/nav/component/main/MainNav';
import Branding from 'src/shared/app/nav/component/branding/Branding';
import Container from 'src/shared/app/base/component/container/Container';
import Plus2Icon from 'src/shared/ui/component/icon/Plus2Icon';
import LinkButton from 'src/shared/ui/component/link/LinkButton';
import ProfileMenu from 'src/shared/app/nav/component/profilemenu/ProfileMenu';
import SkipNavLink from 'src/shared/app/nav/component/skip/SkipNavLink';
import ChevronIcon from 'src/shared/ui/component/icon/ChevronIcon';
import LazyNewTopicMain from 'src/shared/app/topic/new/component/LazyNewTopicMain';
import NotificationsMenu from 'src/shared/app/nav/component/notifications-menu/NotificationsMenu';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import UserAvatar, {
  small,
} from 'src/shared/components/domain/user/avatar/UserAvatar';
import NotificationBadge from 'src/shared/ui/component/messaging/NotificationBadge';

const useStyles = createUseThemeStyles(styles);

const MainTopBar = (): React.Node => {
  const scrollTrigger = useScrollTrigger({ threshold: 25 });
  const location = useLocation();
  const { pathname } = useLocation();
  const intl = useIntl();
  const self = useSelf();

  const [
    newTopicPath,
    peopleFeedPath,
    preferencesPath,
    profilePath,
    ownTopicsPath,
    availabilityPath,
    profileEditPath,
  ] = useRoutes(
    newTopicRoute,
    peopleFeedRoute,
    preferencesRoute,
    profileRoute,
    ownTopicsRoute,
    availabilityRoute,
    profileEditRoute,
  );

  const isProfileActive = [
    preferencesPath,
    profilePath,
    ownTopicsPath,
    availabilityPath,
    profileEditPath,
  ].includes(pathname);

  const [isProfileMenuOpened, setIsProfileMenuOpened] =
    React.useState<boolean>(false);

  const notificationsCount = useSelector(getNotificationCount);
  const hasNotifications = notificationsCount > 0;

  const [notificationsMenuOpened, setNotificationsMenuOpened] =
    React.useState(false);
  const [isProfileMenuFocused, setProfileMenuFocused] = React.useState(false);

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */
  const classes = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const showCreateTopicButton = !['/', peopleFeedPath].includes(
    location.pathname,
  );

  const createTopicBtn = showCreateTopicButton ? (
    <div className={classes.createTopicButtonContainer}>
      <LinkButton
        to={newTopicPath}
        layoutClass={classes.createTopicButon}
        variant="secondary"
        preloadComp={LazyNewTopicMain}
      >
        <Plus2Icon className={classes.createTopicButtonIcon} />
        {intl.formatMessage(messages.newTopic)}
      </LinkButton>
    </div>
  ) : (
    <div className={classes.createTopicButtonContainer}></div>
  );

  return (
    <div
      role="banner"
      className={classNames(classes.root, {
        [classes.rootScroll]: scrollTrigger,
      })}
    >
      <Container>
        <div className={classes.inner}>
          <SkipNavLink />
          <Branding />

          <MainNav className={classes.nav} wrapToContent />

          {createTopicBtn}

          <div className={classes.right}>
            <NotificationsMenu
              isOpened={notificationsMenuOpened}
              setIsOpened={setNotificationsMenuOpened}
              className={classes.notifications}
              withRoundedChild
            >
              {hasNotifications ? (
                <>
                  <NotificationBadge layoutClass={classNames(classes.badge)} />
                </>
              ) : null}
              <NotificationsIcon className={classes.icon} />
            </NotificationsMenu>

            {self && (
              <ProfileMenu
                isMenuOpened={isProfileMenuOpened}
                setIsMenuOpened={setIsProfileMenuOpened}
                setIsFocused={(value) => setProfileMenuFocused(value)}
              >
                <div className={classNames(classes.avatar)}>
                  <div className={classes.profileMenu}>
                    <UserAvatar
                      user={self}
                      variant={small}
                      layoutClass={classNames({
                        [classes.avatarActive]:
                          isProfileActive ||
                          isProfileMenuOpened ||
                          isProfileMenuFocused,
                      })}
                    />
                    <ChevronIcon
                      up={isProfileMenuOpened}
                      down={!isProfileMenuOpened}
                    />
                  </div>
                </div>
              </ProfileMenu>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default compose(React.memo)(MainTopBar);
